import { useMemo, useCallback, useState, FC } from 'react';
import dayjs from 'dayjs';
import { AnimatePresence } from 'motion/react';
import { getUser, localStorageHelper } from '@frontend/auth-helpers';
import { WidgetPromo } from '@frontend/grid-dashboard';
import { useAccessibleWidgetConfig } from './use-accesible-widgets-config';
import { useWidgets } from './use-dashboard-widgets';
import { useWidgetPromo } from './use-widget-promo';
import { widgetLibraryImages } from './widget-library-images-map';

const DISMISSED_STORAGE_KEY = 'widget-promo-dismissed';
const ADDED_WIDGETS_STORAGE_KEY = 'widget-promo-added';

const createUserStorageKey = (baseKey: string, userId: string) => `${baseKey}-${userId}`;

const isWidgetPromoDismissed = (userId: string): boolean => {
  const key = createUserStorageKey(DISMISSED_STORAGE_KEY, userId);
  return !!localStorageHelper.get(key);
};

const dismissPromoWidget = (userId: string): void => {
  const key = createUserStorageKey(DISMISSED_STORAGE_KEY, userId);
  localStorageHelper.create(key, 'true');
};

const getAddedWidgets = (userId: string): Record<string, boolean> => {
  const key = createUserStorageKey(ADDED_WIDGETS_STORAGE_KEY, userId);
  return (localStorageHelper.get(key) as Record<string, boolean>) || {};
};

const addWidgetToStorage = (widgetId: string, userId: string): void => {
  const key = createUserStorageKey(ADDED_WIDGETS_STORAGE_KEY, userId);
  const addedWidgets = getAddedWidgets(userId);
  addedWidgets[widgetId] = true;
  localStorageHelper.create(key, addedWidgets);
};

export const useWidgetPromoLogic = () => {
  const widgetPromo = useWidgetPromo();
  const { widgets } = useWidgets();
  const userId = getUser()?.userID;
  const [isDismissed, setIsDismissed] = useState(() => isWidgetPromoDismissed(userId ?? ''));
  const { accessibleWidgets: widgetConfigs, safeAddWidget: addWidget } = useAccessibleWidgetConfig();

  const widgetPromoKeys = useMemo(() => {
    return Object.keys(widgetPromo) as (keyof typeof widgetPromo)[];
  }, [widgetPromo]);

  const mappedWidgets = useMemo(() => {
    return widgetPromoKeys.map((key) => {
      const size = widgetPromo?.[key]?.promoImage;
      const widgetLibraryImage = widgetLibraryImages[key as keyof typeof widgetLibraryImages];
      const libraryImage = widgetLibraryImage?.[size as keyof typeof widgetLibraryImage];

      return {
        id: key,
        description: widgets[key]?.description || '',
        title: widgets[key]?.title || '',
        libraryImage: libraryImage || null,
        show: (widgetPromo?.[key] as any)?.show ?? widgets[key]?.hasAccess ?? true,
        currentSize: size,
        isEnabled: widgetConfigs.some((config) => config.id === key),
      };
    });
  }, [widgetPromo, widgets, widgetPromoKeys, widgetConfigs]);

  const widgetsToAdd = useMemo(() => {
    const now = dayjs();

    return mappedWidgets.filter((widget) => {
      const shouldShow = widget.show && !widget.isEnabled;
      if (!shouldShow) {
        return false;
      }

      const promoEndDate = widgetPromo[widget.id as keyof typeof widgetPromo]?.promoEndDate;
      if (promoEndDate) {
        return now.isBefore(dayjs(promoEndDate));
      }
      return true;
    });
  }, [mappedWidgets, widgetConfigs, widgetPromo]);

  const handleDismiss = useCallback(() => {
    if (userId) {
      dismissPromoWidget(userId);
      setIsDismissed(true);
    }
  }, [userId]);

  const handleAddWidget = useCallback(
    (widgetId: string) => {
      if (userId) {
        addWidgetToStorage(widgetId, userId);

        const addedWidgets = getAddedWidgets(userId);
        const allWidgetsAdded = widgetsToAdd.every(
          (widget) => !!addedWidgets[widget.id] || !!widgetConfigs.find((config) => config.id === widget.id)
        );

        if (allWidgetsAdded) {
          handleDismiss();
        }

        setTimeout(() => {
          document.querySelector(`#dashboard-widget-${widgetId}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });
        }, 100);
      }

      const targetWidget = widgetsToAdd.find((widget) => widget.id === widgetId);
      addWidget({
        id: widgetId,
        size: targetWidget?.currentSize || 'medium-wide',
      });
    },
    [userId, widgetsToAdd, addWidget, widgetConfigs, handleDismiss]
  );

  return {
    widgetsToAdd,
    isDismissed,
    handleDismiss,
    handleAddWidget,
  };
};

interface WidgetPromoProps {
  innerRef?: React.LegacyRef<HTMLDivElement>; // This is required for priority renderer mount check
  onDismiss?: () => void;
}
export const WidgetPromotion: FC<WidgetPromoProps> = ({ innerRef, onDismiss }) => {
  const { widgetsToAdd, isDismissed, handleDismiss, handleAddWidget } = useWidgetPromoLogic();

  return isDismissed || widgetsToAdd.length === 0 ? null : (
    <AnimatePresence>
      <WidgetPromo
        innerRef={innerRef}
        onDismiss={() => {
          handleDismiss();
          onDismiss?.();
        }}
        onAddWidget={handleAddWidget}
        widgets={widgetsToAdd}
      />
    </AnimatePresence>
  );
};
